import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { filter, firstValueFrom, map, race } from 'rxjs';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { ModalInstance, ModalService } from '../../core/services/modal.service';
import { SavableBeforeExit } from '../interfaces/savable-before-exit';
import { isPlatformBrowser } from '@angular/common';
import { DirtyTrackingService } from '../../core/services/dirty-tracking.service';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class RoomStepDirtyGuard {
  platformId = inject(PLATFORM_ID);
  private dirtyTrackingService = inject(DirtyTrackingService);
  private modalService = inject(ModalService);
  private translocoService = inject(TranslocoService);

  // Confirmation en cours : un second clic n'ouvre pas une seconde modale
  private pendingDecision: Promise<boolean> | null = null;

  constructor() {}

  canDeactivate(
    component: SavableBeforeExit,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot,
  ): Promise<boolean> {
    // La navigation précédente est annulée par la nouvelle : celle-ci reprend sa réponse
    this.pendingDecision ??= this.confirmLeave(component).finally(() => {
      this.pendingDecision = null;
    });

    return this.pendingDecision;
  }

  private async confirmLeave(component: SavableBeforeExit): Promise<boolean> {
    const isDirty: boolean = this.dirtyTrackingService.isDirty;
    if (isDirty && this.canSaveBeforeExit(component)) {
      const modalInstance: ModalInstance = this.modalService.open(
        ConfirmModalComponent,
        {
          title: this.translocoService.translate('common.unsaved-changes.title'),
          message: this.translocoService.translate('common.unsaved-changes.message'),
          okAction: {
            title: this.translocoService.translate('common.button.save'),
          },
          cancelAction: {
            title:
              isPlatformBrowser(this.platformId) && window.innerWidth < 600
                ? this.translocoService.translate('common.unsaved-changes.leave-short')
                : this.translocoService.translate('common.unsaved-changes.leave-long'),
          },
          closeOnBackdropClick: false,
        },
      );

      const ok: boolean | null = await firstValueFrom(
        race(
          modalInstance.actionClicked$,
          // Échap ferme la modale sans répondre : on reste sur la page
          this.modalService.watcher$.pipe(
            filter(
              (event) => event.type === 'close' && event.modal === modalInstance,
            ),
            map(() => null),
          ),
        ),
      );

      if (ok === null) {
        return false;
      }
      if (ok) {
        modalInstance.saving = true;
        const changesSaved: boolean = await component.saveChanges();
        modalInstance.saving = false;
        if (changesSaved) {
          this.dirtyTrackingService.reset();
          this.modalService.close();
          return true;
        } else {
          this.modalService.close();
          return false;
        }
      } else {
        this.dirtyTrackingService.reset();
        return true;
      }
    } else {
      this.dirtyTrackingService.reset();
      return true;
    }
  }

  private canSaveBeforeExit(component: SavableBeforeExit): boolean {
    return typeof component?.saveChanges === 'function';
  }
}
